import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Download, 
  Upload, 
  Database, 
  Store, 
  RefreshCw, 
  History,
  CheckCircle,
  AlertTriangle,
  FileJson
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BackupPage = () => {
  const { user, isAdmin, isSuperUser } = useAuth();
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [backupAll, setBackupAll] = useState(false);
  const [canBackupAll, setCanBackupAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [history, setHistory] = useState([]);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showConfirmRestore, setShowConfirmRestore] = useState(false);
  const [restoreMode, setRestoreMode] = useState('merge');
  const [backupFile, setBackupFile] = useState(null);
  const [backupPreview, setBackupPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchStores();
    fetchHistory();
  }, []);

  const fetchStores = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/backup/stores`);
      setStores(response.data.stores || []);
      setCanBackupAll(response.data.can_backup_all);
    } catch (error) {
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/backup/history`);
      setHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load backup history');
    }
  };

  const handleStoreToggle = (storeId) => {
    setSelectedStores(prev => 
      prev.includes(storeId) 
        ? prev.filter(id => id !== storeId)
        : [...prev, storeId]
    );
    setBackupAll(false);
  };

  const handleBackupAll = (checked) => {
    setBackupAll(checked);
    if (checked) {
      setSelectedStores(stores.map(s => s.id));
    } else {
      setSelectedStores([]);
    }
  };

  const handleCreateBackup = async () => {
    if (selectedStores.length === 0 && !backupAll) {
      toast.error('Please select at least one store');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/backup/create`, {
        store_ids: selectedStores,
        backup_all: backupAll
      });

      // Download the backup file
      const backupData = response.data;
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `promortek_backup_${format(new Date(), 'yyyyMMdd_HHmmss')}.json`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Backup created for ${backupData.store_count} store(s)`);
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backup failed');
    } finally {
      setCreating(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Please select a valid JSON backup file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!data.backup_id || !data.stores) {
          toast.error('Invalid backup file format');
          return;
        }
        setBackupFile(data);
        setBackupPreview({
          backup_id: data.backup_id,
          created_at: data.created_at,
          created_by: data.created_by,
          store_count: data.store_count,
          stores: data.stores.map(s => ({
            id: s.store?.id,
            name: s.store?.name,
            store_type: s.store?.store_type,
            records: {
              sales: s.sales_entries?.length || 0,
              creditors: s.creditors?.length || 0,
              transactions: (s.creditor_transactions?.length || 0) + (s.investment_transactions?.length || 0),
              purchases: s.purchases?.length || 0,
              suppliers: s.suppliers?.length || 0,
              fuel_deliveries: s.fuel_deliveries?.length || 0,
              money_movements: s.money_movements?.length || 0
            }
          }))
        });
        setShowRestoreDialog(true);
      } catch (err) {
        toast.error('Failed to parse backup file');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleRestore = async () => {
    if (!backupFile) return;

    setRestoring(true);
    try {
      const response = await axios.post(`${API}/backup/restore`, {
        backup_data: backupFile,
        restore_mode: restoreMode
      });

      const result = response.data;
      if (result.restored_stores?.length > 0) {
        toast.success(`Restored ${result.restored_stores.length} store(s) successfully`);
      }
      if (result.skipped_stores?.length > 0) {
        toast.warning(`Skipped ${result.skipped_stores.length} store(s) due to access restrictions`);
      }

      setShowRestoreDialog(false);
      setShowConfirmRestore(false);
      setBackupFile(null);
      setBackupPreview(null);
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  if (!isAdmin && !isSuperUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Backup & Restore
          </h1>
          <p className="text-muted-foreground mt-1">
            Create backups and restore store data
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchStores(); fetchHistory(); }} data-testid="refresh-backup-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Create Backup Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Create Backup
            </CardTitle>
            <CardDescription>
              Select stores to include in the backup
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Backup All Option (Super User Only) */}
            {canBackupAll && (
              <div className="flex items-center space-x-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <Checkbox
                  id="backup-all"
                  checked={backupAll}
                  onCheckedChange={handleBackupAll}
                  data-testid="backup-all-checkbox"
                />
                <Label htmlFor="backup-all" className="font-medium cursor-pointer">
                  Backup All Stores ({stores.length})
                </Label>
              </div>
            )}

            {/* Store Selection */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {stores.map(store => (
                <div
                  key={store.id}
                  className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <Checkbox
                    id={`store-${store.id}`}
                    checked={selectedStores.includes(store.id)}
                    onCheckedChange={() => handleStoreToggle(store.id)}
                    disabled={backupAll}
                    data-testid={`store-checkbox-${store.id}`}
                  />
                  <Label htmlFor={`store-${store.id}`} className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Store className="h-4 w-4 text-muted-foreground" />
                      <span>{store.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {store.store_type?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </Label>
                </div>
              ))}
            </div>

            {/* Create Button */}
            <Button
              className="w-full"
              onClick={handleCreateBackup}
              disabled={creating || (selectedStores.length === 0 && !backupAll)}
              data-testid="create-backup-btn"
            >
              {creating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Creating Backup...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Create Backup ({backupAll ? 'All' : selectedStores.length} stores)
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Restore Backup Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Restore from Backup
            </CardTitle>
            <CardDescription>
              Upload a backup file to restore store data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileJson className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-2">
                Click to select a backup file
              </p>
              <p className="text-xs text-muted-foreground">
                JSON format (.json)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="restore-file-input"
              />
            </div>

            {/* Restore Mode */}
            <div className="space-y-2">
              <Label>Restore Mode</Label>
              <Select value={restoreMode} onValueChange={setRestoreMode}>
                <SelectTrigger data-testid="restore-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Merge (Keep existing, add new)
                    </div>
                  </SelectItem>
                  <SelectItem value="replace">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Replace (Delete existing data)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {restoreMode === 'merge' 
                  ? 'Existing data will be preserved. Only new records will be added.'
                  : 'Warning: Existing data for selected stores will be deleted before restore.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Backup History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Backup & Restore History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No backup history found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Action</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">User</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((log, idx) => (
                    <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                      <td className="p-3 text-sm">
                        {new Date(log.timestamp).toLocaleString('en-GB')}
                      </td>
                      <td className="p-3">
                        <Badge className={log.entity_type === 'backup' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}>
                          {log.entity_type}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm">{log.user_name}</td>
                      <td className="p-3 text-sm text-muted-foreground">{log.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restore Preview Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Restore Backup</DialogTitle>
            <DialogDescription>
              Review the backup contents before restoring
            </DialogDescription>
          </DialogHeader>
          
          {backupPreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Backup Date</p>
                  <p className="font-medium">
                    {new Date(backupPreview.created_at).toLocaleString('en-GB')}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Created By</p>
                  <p className="font-medium">{backupPreview.created_by?.user_name || 'Unknown'}</p>
                </div>
              </div>

              <div>
                <p className="text-muted-foreground mb-2">Stores in Backup ({backupPreview.store_count})</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {backupPreview.stores.map((store, idx) => (
                    <div key={idx} className="p-2 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{store.name}</p>
                        <Badge variant="outline" className="text-xs">
                          {store.store_type?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-1">
                        <span>Sales: {store.records.sales}</span>
                        <span>Creditors: {store.records.creditors}</span>
                        <span>Transactions: {store.records.transactions}</span>
                        <span>Purchases: {store.records.purchases}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-sm font-medium text-amber-400">
                  Mode: {restoreMode === 'merge' ? 'Merge' : 'Replace'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {restoreMode === 'merge' 
                    ? 'Only new records will be added. Existing data will be preserved.'
                    : 'Existing data for these stores will be deleted before restore.'}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => setShowConfirmRestore(true)}
              disabled={restoring}
            >
              {restoring ? 'Restoring...' : 'Restore'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Restore Dialog */}
      <AlertDialog open={showConfirmRestore} onOpenChange={setShowConfirmRestore}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Restore</AlertDialogTitle>
            <AlertDialogDescription>
              {restoreMode === 'replace' 
                ? 'This will DELETE all existing data for the selected stores and restore from the backup. This action cannot be undone.'
                : 'This will add new records from the backup to your existing data.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore} disabled={restoring}>
              {restoring ? 'Restoring...' : 'Confirm Restore'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupPage;
